// Validation utility functions

/**
 * Validates if a value is empty
 * @param {*} value - Value to check
 * @returns {boolean} True if value is empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Validates if a value meets required criteria
 * @param {*} value - Value to validate
 * @param {string} fieldName - Name of the field for error message
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateRequired = (value, fieldName) => {
  return isEmpty(value) ? `${fieldName} is required` : null;
};

/**
 * Validates phone number format
 * @param {string} phone - Phone number to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePhone = (phone) => {
  if (isEmpty(phone)) return null; // Skip if empty (use validateRequired for required fields)
  // Allows +, spaces, hyphens, and parentheses, requires 8-20 digits
  const phoneRegex = /^\+?[0-9\s\-()]{8,20}$/;
  return phoneRegex.test(phone) ? null : 'Invalid phone number format';
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateEmail = (email) => {
  if (isEmpty(email)) return null;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) ? null : 'Invalid email format';
};

/**
 * Validates website URL format
 * @param {string} website - Website URL to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateWebsite = (website) => {
  if (isEmpty(website)) return null;
  const websiteRegex = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
  return websiteRegex.test(website) ? null : 'Invalid website format';
};

/**
 * Validates password strength
 * @param {string} password - Password to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecial = true
  } = options;

  if (isEmpty(password)) return null;

  const errors = [];

  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters`);
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  if (requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  return errors.length > 0 ? errors.join(', ') : null;
};

/**
 * Validates if two passwords match
 * @param {string} password - Password
 * @param {string} confirmPassword - Password confirmation
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  if (isEmpty(password) || isEmpty(confirmPassword)) return null;
  return password === confirmPassword ? null : 'Passwords do not match';
};

/**
 * Validates postal code format
 * @param {string} postalCode - Postal code to validate
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validatePostalCode = (postalCode) => {
  if (isEmpty(postalCode)) return null;
  // Allows 5-10 digits, removing any spaces
  return /^[0-9]{5,10}$/.test(postalCode.replace(/\s/g, '')) ? null : 'Invalid postal code format';
};

/**
 * Validates text length
 * @param {string} text - Text to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateLength = (text, options = {}) => {
  const { min, max, fieldName = 'Text' } = options;
  
  if (isEmpty(text)) return null;
  
  if (min && text.length < min) {
    return `${fieldName} must be at least ${min} characters`;
  }
  
  if (max && text.length > max) {
    return `${fieldName} must not exceed ${max} characters`;
  }
  
  return null;
};

/**
 * Validates a form field based on its type and requirements
 * @param {string} fieldName - Name of the field
 * @param {*} value - Value to validate
 * @param {Object} options - Validation options
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateField = (fieldName, value, options = {}) => {
  const {
    required = false,
    type = 'text',
    minLength,
    maxLength,
    passwordOptions,
    confirmPassword
  } = options;

  // Check required first
  if (required) {
    const requiredError = validateRequired(value, fieldName);
    if (requiredError) return requiredError;
  }

  // Skip other validations if empty and not required
  if (isEmpty(value) && !required) return null;

  // Validate based on type
  switch (type) {
    case 'email':
      return validateEmail(value);
    case 'phone':
      return validatePhone(value);
    case 'website':
      return validateWebsite(value);
    case 'password':
      const passwordError = validatePassword(value, passwordOptions);
      if (passwordError) return passwordError;
      if (confirmPassword) {
        return validatePasswordMatch(value, confirmPassword);
      }
      return null;
    case 'postalCode':
      return validatePostalCode(value);
    case 'text':
    default:
      if (minLength || maxLength) {
        return validateLength(value, { min: minLength, max: maxLength, fieldName });
      }
      return null;
  }
};

/**
 * ✅ BLOCKED WORDS: Validates project name for blocked words
 * @param {string} projectName - Project name to validate
 * @returns {string|null} Error message if blocked words found, null if valid
 */
export const validateProjectNameBlockedWords = (projectName) => {
  if (isEmpty(projectName)) return null;
  
  // Block-list for "consult / consulting" (similar forms only)
  const consultBlockList = [
    'consult', 'consults', 'consulted', 'consulting', 'consultant', 'consultants',
    'consultancy', 'consultative', 'consultatives', 'consultation', 'consultations',
    'consultative-service', 'consultor', 'consultive'
  ];
  
  // Block-list for "implement / implementation" (similar forms only)
  const implementBlockList = [
    'implement', 'implements', 'implemented', 'implementing', 'implementation',
    'implementations', 'implementer', 'implementors', 'implementor', 'implementable',
    'reimplement', 'reimplementation', 'reimplemented', 'reimplementing'
  ];
  
  // ✅ INDONESIAN SPELLINGS: Block-list for Indonesian spellings of "consult"
  const consultIndonesianBlockList = [
    'konsul', 'konsultan', 'konsultan-konsultan', 'konsultasi', 'konsultasinya',
    'mengkonsultasi', 'dikonsultasi', 'dikonsultasikan', 'mengkonsultasikan'
  ];
  
  // ✅ INDONESIAN SPELLINGS: Block-list for Indonesian spellings of "implement"
  const implementIndonesianBlockList = [
    'implementasi', 'pengimplementasian', 'diimplementasi', 'diimplementasikan',
    'mengimplementasi', 'mengimplementasikan'
  ];
  
  // ✅ VARIATIONS/TYPOS: Block-list for variations and typos
  const variationsBlockList = [
    'konstul', 'konstultan', 'konsultan2', 'konsul2', 'konsut', 'konsult',
    'konstultasi', 'konsultasiin', 'konsulasi', 'konsulasiin',
    'implementa', 'implentasi', 'implemetasi', 'implementasii', 'implementasinya',
    'imple', 'implemen', 'impelementasi', 'impementasi', 'implemntasi'
  ];
  
  const allBlockedWords = [
    ...consultBlockList,
    ...implementBlockList,
    ...consultIndonesianBlockList,
    ...implementIndonesianBlockList,
    ...variationsBlockList
  ];
  
  // Convert project name to lowercase for case-insensitive matching
  const projectNameLower = projectName.toLowerCase();
  
  // Check for blocked words (whole word match using word boundaries)
  const foundBlockedWords = allBlockedWords.filter(word => {
    // Use word boundary regex to match whole words only
    const regex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(projectNameLower);
  });
  
  if (foundBlockedWords.length > 0) {
    return `Project name contains blocked word(s): ${foundBlockedWords.join(', ')}. Please use different wording.`;
  }
  
  return null;
};

/**
 * Validates a lead form data object
 * @param {Object} formData - The form data to validate
 * @param {Array} serviceSections - Array of service section indices
 * @returns {Object} Object containing validation errors
 */
export const validateLeadForm = (formData, serviceSections) => {
  const errors = {};
  
  // Company Information Validation
  errors.company = validateRequired(formData.company, 'Client');
  errors.contact = validateRequired(formData.contact, 'Contact Name');
  errors.contactPhoneNumber = validateRequired(formData.contactPhoneNumber, 'Contact Phone Number');
  if (formData.contactPhoneNumber) {
    errors.contactPhoneNumber = validatePhone(formData.contactPhoneNumber);
  }
  errors.email = validateRequired(formData.email, 'Contact Email');
  if (formData.email) {
    errors.email = validateEmail(formData.email);
  }
  errors.address = validateRequired(formData.address, 'Address');
  errors.companyPhoneNumber = validateRequired(formData.companyPhoneNumber, 'Company Phone Number');
  if (formData.companyPhoneNumber) {
    errors.companyPhoneNumber = validatePhone(formData.companyPhoneNumber);
  }
  // errors.sector = validateRequired(formData.sector, 'Sector');
  // if (formData.companyWebsite) {
  //   errors.companyWebsite = validateWebsite(formData.companyWebsite);
  // }

  // Project Information Validation
  errors.stage = validateRequired(formData.stage, 'Stage');
  errors.leadSource = validateRequired(formData.leadSource, 'Lead Source');
  errors.projectName = validateRequired(formData.projectName, 'Project Name');
  
  // ✅ BLOCKED WORDS: Validate project name for blocked words
  if (formData.projectName) {
    const blockedWordsError = validateProjectNameBlockedWords(formData.projectName);
    if (blockedWordsError) {
      errors.projectName = blockedWordsError;
    }
  }
  
  errors.projectDescription = validateRequired(formData.projectDescription, 'Project Description');
  if (formData.projectDescription) {
    const descError = validateLength(formData.projectDescription, {
      min: 10,
      fieldName: 'Project Description'
    });
    if (descError) errors.projectDescription = descError;
  }
  errors.assignedUser = validateRequired(formData.assignedUser, 'Assigned To');

  // Services Validation
  serviceSections.forEach((sectionIndex) => {
    errors[`services-${sectionIndex}`] = validateRequired(formData[`services-${sectionIndex}`], 'Services');
    errors[`standards-${sectionIndex}`] = validateRequired(formData[`standards-${sectionIndex}`], 'Standards');
    errors[`scope-${sectionIndex}`] = validateScopeRows(formData[`scope-${sectionIndex}`]);
  });

  // Remove null errors
  Object.keys(errors).forEach(key => {
    if (errors[key] === null) {
      delete errors[key];
    }
  });

  return errors;
};

/**
 * Validates a per-standard scope value. Scope is stored as one entry per
 * standard row: each entry is either a string (regular service) or an array
 * of scope-item ids (ISCC/Sustainability service). Each row is optional, but
 * a non-empty string row must meet the minimum length.
 * @param {*} scopeValue - The scope-${sectionIndex} form value (array of rows)
 * @returns {string|null} Error message if invalid, null if valid
 */
export const validateScopeRows = (scopeValue) => {
  if (!Array.isArray(scopeValue)) {
    if (typeof scopeValue === 'string' && scopeValue) {
      return validateLength(scopeValue, { min: 10, fieldName: 'Scope' });
    }
    return null;
  }

  for (const rowScope of scopeValue) {
    if (typeof rowScope === 'string' && rowScope.trim()) {
      const scopeError = validateLength(rowScope, { min: 10, fieldName: 'Scope' });
      if (scopeError) return scopeError;
    }
  }
  return null;
};

/**
 * Validates a service section
 * @param {Object} formData - The form data to validate
 * @param {number} sectionIndex - The index of the service section
 * @returns {Object} Object containing validation errors for the section
 */
export const validateServiceSection = (formData, sectionIndex) => {
  const errors = {};

  errors[`services-${sectionIndex}`] = validateRequired(formData[`services-${sectionIndex}`], 'Services');
  errors[`standards-${sectionIndex}`] = validateRequired(formData[`standards-${sectionIndex}`], 'Standards');
  errors[`scope-${sectionIndex}`] = validateScopeRows(formData[`scope-${sectionIndex}`]);

  // Remove null errors
  Object.keys(errors).forEach(key => {
    if (errors[key] === null) {
      delete errors[key];
    }
  });

  return errors;
};

/**
 * Validates an invoice form data object
 * @param {Object} formData - The form data to validate
 * @returns {Object} Object containing validation errors
 */
export const validateInvoiceForm = (formData) => {
  const errors = {};
  
  // Basic Information Validation
  errors.client = validateRequired(formData.client, 'Client');
  errors.projectName = validateRequired(formData.projectName, 'Project');
  errors.billDate = validateRequired(formData.billDate, 'Bill Date');
  errors.dueDate = validateRequired(formData.dueDate, 'Due Date');
  
  // Date validation - due date should be after bill date
  if (formData.billDate && formData.dueDate) {
    const billDate = new Date(formData.billDate);
    const dueDate = new Date(formData.dueDate);
    if (dueDate <= billDate) {
      errors.dueDate = 'Due date must be after bill date';
    }
  }
  
  // Tax validation - should be between 0 and 100
  if (formData.tax !== undefined && formData.tax !== null && formData.tax !== '') {
    const taxValue = parseFloat(formData.tax);
    if (isNaN(taxValue) || taxValue < 0 || taxValue > 100) {
      errors.tax = 'Tax percentage must be between 0 and 100';
    }
  }
  
  // Description validation - optional but if provided, should have minimum length (no max limit)
  if (formData.description && formData.description.trim()) {
    const descError = validateLength(formData.description, {
      min: 10,
      max: null, // No maximum character limit
      fieldName: 'Description'
    });
    if (descError) errors.description = descError;
  }
  
  // Document validation - required for new invoices, optional for edit
  if (!formData.isEditMode && (!formData.documents || formData.documents.length === 0)) {
    errors.documents = 'Document is required for new invoices';
  }
  
  // Remove null errors
  Object.keys(errors).forEach(key => {
    if (errors[key] === null) {
      delete errors[key];
    }
  });

  return errors;
}; 